import classNames from 'classnames'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import ContentfulAdditionalSizeChartGuide from '@src/quince-site-common/src/domains/contentful/contentful-additional-size-chart-guide'
import ContentfulInternationalSizeConversionGuide from '@src/quince-site-common/src/domains/contentful/contentful-international-size-conversion-guide'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import styles from './sizeChartV2.module.scss'

export interface SizeChartV2ScrollState {
  showScrollButtons: boolean
  canScrollLeft: boolean
  canScrollRight: boolean
}

export interface SizeChartV2Handle {
  scrollLeft: () => void
  scrollRight: () => void
}

interface SizeChartV2Props {
  activeSizeGuideUnitIndex: number
  onScrollStateChange?: (state: SizeChartV2ScrollState) => void
  sizeChart: ContentfulSizeChart | ContentfulInternationalSizeConversionGuide | ContentfulAdditionalSizeChartGuide
  subtitle?: string
  description?: string
}

const MOBILE_SMALL_CONTAINER_THRESHOLD = 432
const MOBILE_FIT_COLUMNS_SMALL_CONTAINER = 4
const MOBILE_FIT_COLUMNS_LARGE_CONTAINER = 5
const DESKTOP_FIT_COLUMNS = 8

const splitValues = (values: string): string[] =>
  values
    .split(',')
    .map(value => value.trim())
    .filter(Boolean)

const getFitColumnLimit = (isMobile: boolean, containerWidth: number) => {
  if (!isMobile) return DESKTOP_FIT_COLUMNS

  return containerWidth < MOBILE_SMALL_CONTAINER_THRESHOLD
    ? MOBILE_FIT_COLUMNS_SMALL_CONTAINER
    : MOBILE_FIT_COLUMNS_LARGE_CONTAINER
}

const isMutedRow = (rowIndex: number) => rowIndex % 2 === 0

const SCROLL_EDGE_THRESHOLD = 2

const cellClass = (opts: { isHeader?: boolean; isFirstColumn?: boolean; rowIndex: number }) =>
  classNames(
    styles.sizeChartV2__cell,
    opts.isHeader && styles.sizeChartV2__cellHeader,
    opts.isFirstColumn && styles.sizeChartV2__cellFirstColumn,
    isMutedRow(opts.rowIndex) && styles.sizeChartV2__cellMuted,
  )

const SizeChartV2 = forwardRef<SizeChartV2Handle, SizeChartV2Props>(
  ({ activeSizeGuideUnitIndex, sizeChart, onScrollStateChange, subtitle, description }, ref) => {
    const tableWrapperRef = useRef<HTMLDivElement | null>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const { deviceType } = useDeviceInfo()
    const isMobile = deviceType === DEVICE_TYPES.MOBILE

    const activeSizeChartItems = sizeChart.sections[activeSizeGuideUnitIndex]?.items

    const { headerRow, dataRows } = useMemo(() => {
      const parsed = (activeSizeChartItems ?? []).map(row => ({
        key: row.key,
        values: splitValues(row.values ?? ''),
      }))

      const [sourceHeader, ...sourceData] = parsed

      return {
        headerRow: {
          key: sourceHeader?.key ?? '',
          values: sourceData.map(row => row.key),
        },
        dataRows: (sourceHeader?.values ?? []).map((sizeValue, valueIndex) => ({
          key: sizeValue,
          values: sourceData.map(row => row.values[valueIndex] ?? ''),
        })),
      }
    }, [activeSizeChartItems])

    const columnCount = headerRow.values.length + 1

    const fitColumnLimit = getFitColumnLimit(isMobile, containerWidth)
    const isScrollMode = columnCount > fitColumnLimit
    const showScrollButtons = isScrollMode && !isMobile

    const emitScrollState = useCallback(() => {
      const wrapper = tableWrapperRef.current
      if (!wrapper) return

      const nextCanScrollRight = wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth - SCROLL_EDGE_THRESHOLD
      setCanScrollRight(nextCanScrollRight)

      onScrollStateChange?.({
        showScrollButtons,
        canScrollLeft: wrapper.scrollLeft > SCROLL_EDGE_THRESHOLD,
        canScrollRight: nextCanScrollRight,
      })
    }, [onScrollStateChange, showScrollButtons])

    const scrollByPage = (direction: 1 | -1) => {
      const wrapper = tableWrapperRef.current
      if (!wrapper) return
      wrapper.scrollBy({ left: direction * wrapper.clientWidth, behavior: 'smooth' })
    }

    useImperativeHandle(
      ref,
      () => ({
        scrollLeft: () => {
          scrollByPage(-1)
        },
        scrollRight: () => {
          scrollByPage(1)
        },
      }),
      [],
    )

    useEffect(() => {
      const tableWrapper = tableWrapperRef.current
      if (!tableWrapper) return undefined

      const updateWidth = () => {
        setContainerWidth(tableWrapper.clientWidth)
      }

      updateWidth()
      emitScrollState()

      tableWrapper.addEventListener('scroll', emitScrollState, { passive: true })

      const resizeObserver = new ResizeObserver(() => {
        updateWidth()
        emitScrollState()
      })
      resizeObserver.observe(tableWrapper)

      return () => {
        resizeObserver.disconnect()
        tableWrapper.removeEventListener('scroll', emitScrollState)
      }
    }, [emitScrollState])

    return (
      <div className={styles.sizeChartV2} data-testid={TEST_LOCATORS.SIZE_CHART}>
        {subtitle && <div className={styles.sizeChartV2__subtitle}>{subtitle}</div>}
        {description && <div className={styles.sizeChartV2__description}>{description}</div>}
        <div className={classNames(styles.sizeChartV2__tableViewport, canScrollRight && styles.sizeChartV2__fadeRight)}>
          <div
            ref={tableWrapperRef}
            className={classNames(
              styles.sizeChartV2__tableWrapper,
              isScrollMode && styles.sizeChartV2__tableWrapperScroll,
            )}
          >
            <table className={classNames(styles.sizeChartV2__table, isScrollMode && styles.sizeChartV2__tableScroll)}>
              <thead>
                <tr className={styles.sizeChartV2__row}>
                  <th className={cellClass({ isHeader: true, isFirstColumn: true, rowIndex: 0 })}>
                    <span className={styles.sizeChartV2__cellText}>{headerRow.key}</span>
                  </th>
                  {headerRow.values.map((value, index) => (
                    <th key={`${value}__header__${index}`} className={cellClass({ isHeader: true, rowIndex: 0 })}>
                      <span className={styles.sizeChartV2__cellText}>{value}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, rowIndex) => (
                  <tr key={row.key} className={styles.sizeChartV2__row}>
                    <th className={cellClass({ isFirstColumn: true, rowIndex: rowIndex + 1 })}>
                      <span className={styles.sizeChartV2__cellText}>{row.key}</span>
                    </th>
                    {row.values.map((value, index) => (
                      <td key={`${row.key}__${value}__${index}`} className={cellClass({ rowIndex: rowIndex + 1 })}>
                        <span className={styles.sizeChartV2__cellText}>{value}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  },
)

SizeChartV2.displayName = 'SizeChartV2'

export default SizeChartV2
